import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './protectedRoute';
import Dashboard from '@/containers/dashboard';
import Product from '@/containers/product';
import Login from '@/containers/login';
import AppLayout from '@/layout/layout';
import CompanySetting from '@/containers/setting/company';
import Customer from '@/containers/customer/search';
import ProductDetail from '@/containers/product/product-detail';
import Order from '@/containers/order';
import CreateOrder from '@/containers/order/maintain/create-order';
import CreateExportOrder from '@/containers/export/maintain/create-order';
import { EPermission } from '@/enums/EPermission';
import { useAuth } from '@/contexts/AuthContext';
import { EUserRole } from '@/enums/EUserRole';
import NotFound from '../containers/pages/404';
import UserSetting from '@/containers/setting/user';
import UnitSetting from '@/containers/setting/unit';
import BusinessUnit from '@/containers/setting/business-unit';
import CustomerMaintain from '@/containers/customer/maintain';
import Stock from '@/containers/stock/search';
import ImportData from '@/containers/import';
import SalesReport from '../containers/report/sales-report';
import SalesReport2 from '../containers/report/sales-report-2';
import StockReport from '@/containers/report/stock-report';
import OrderDetail from '@/containers/order/maintain';
import ExportDetail from '@/containers/export/maintain';
import Collector from '@/containers/collector/search';
import Exportor from '@/containers/export';

import { mainMenu } from '@/configs/menuItems';
import CollectorReport from '@/containers/report/collector-report';
import Inbound from '@/containers/inbound';
import InboundMaintain from '@/containers/inbound/maintain/inbound-maintain';
import GoodReceipt from '@/containers/good-receive';
import GoodReceiptCreate from '@/containers/good-receive/maintain/create';
import CustomerOrderReport from '@/containers/report/customer-order-report';
import FinancialReport from '@/containers/report/financial-report';
import ProductAllocation from '@/containers/product-allocation';
import ExchangeRate from '@/containers/setting/exchange-reate';
import NotePurchaseReport from '@/containers/report/note-purchase-report';
import ProductAllocationFiles from '@/containers/product-allocation-file';
import FinancialMatchingReport from '@/containers/report/financial-matching-report';
import StockMatchingReport from '@/containers/report/stock-matching-report';
import CndnNote from '@/containers/cndn-note';
import CreateCndn from '@/containers/cndn-note/maintain/create';
import ViewCndn from '@/containers/cndn-note/maintain/view';
import BillingNote from '@/containers/billing-note';
import CreateBillingNote from '@/containers/billing-note/maintain/create';
import ViewBillingNote from '@/containers/billing-note/maintain/view';
import DoOrderReport from '@/containers/report/do-report';
import EmailFollowUpSetting from '@/containers/setting/email-follow-up';
import Return from '@/containers/return';
import Location from '@/containers/location';
import Reserved from '@/containers/reserved';
import MaintainReserved from '@/containers/reserved/maintain';
import CndnFreeForm from '@/containers/cndn-free-form';
import CreateCndnFreeForm from '@/containers/cndn-free-form/maintain/create';
import ViewCndnFreeform from '@/containers/cndn-free-form/maintain/components/view';
import FreeForm from '@/containers/free-form';
import OrderFreeFrom from '@/containers/order-free-form';
import CrateOrderFreeForm from '@/containers/order-free-form/maintain/create';
import OrderManualDetail from '@/containers/order-free-form/maintain/view';

const dashbaordMenu = mainMenu.find(c => c.key === 'dashboard');
const orderMenu = mainMenu.find(c => c.key === 'order');
const exportMenu = mainMenu.find(c => c.key === 'export');
const customerMenu = mainMenu.find(c => c.key === 'customer');
const productMenu = mainMenu.find(c => c.key === 'product');
const stockMenu = mainMenu.find(c => c.key === 'stock');
const stockReportMenu = mainMenu.find(c => c.key === 'stock-report');
const companyReportMenu = mainMenu.find(c => c.key === 'company');
const baseUnitReportMenu = mainMenu.find(c => c.key === 'base-unit');
const buMenu = mainMenu.find(c => c.key === 'bu');
const importDataMenu = mainMenu.find(c => c.key === 'import-data');
const userDataMenu = mainMenu.find(c => c.key === 'user');
const collectorMenu = mainMenu.find(c => c.key === 'collector');
const collectorReportMenu = mainMenu.find(c => c.key === 'collector-report');
const customerOrderRerport = mainMenu.find(
  c => c.key === 'customer-order-report',
);
const salesReport = mainMenu.find(c => c.key === 'sales-report');
const inboundMenu = mainMenu.find(c => c.key === 'inbound');
const returnMenu = mainMenu.find(c => c.key === 'return');
const locationMenu = mainMenu.find(c => c.key === 'location');
const cndnNoteMenu = mainMenu.find(c => c.key === 'cndn-note');
const billingNoteMenu = mainMenu.find(c => c.key === 'billing-note');
const cndnFreeFormMenu = mainMenu.find(c => c.key === 'cndn-free-form');
const freeFormMenu = mainMenu.find(c => c.key === 'free-form');

const RoutesConfig: React.FC = () => {
  const { isAuthenticated, userInfo } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          !isAuthenticated ? (
            <Navigate to="/login" />
          ) : userInfo?.role === EUserRole.Warehouse ? (
            <Navigate to="/order" />
          ) : userInfo?.role === EUserRole.Collector ? (
            <Navigate to="/collector" />
          ) : (
            <Navigate to="/dashboard" />
          )
        }
      />
      <Route
        path="/*"
        element={
          <AppLayout>
            <Routes>
              <Route
                path="dashboard"
                element={
                  <ProtectedRoute
                    element={<Dashboard />}
                    permissions={[EPermission.Allow]}
                    roles={dashbaordMenu?.roles || []}
                  />
                }
              />
              <Route
                path="product"
                element={
                  <ProtectedRoute
                    element={<Product />}
                    permissions={[EPermission.Allow]}
                    roles={productMenu?.roles || []}
                  />
                }
              />
              <Route
                path="product/:id"
                element={
                  <ProtectedRoute
                    element={<ProductDetail />}
                    permissions={[EPermission.Allow]}
                    roles={productMenu?.roles || []}
                  />
                }
              />
              <Route
                path="product-allocations"
                element={
                  <ProtectedRoute
                    element={<ProductAllocation />}
                    permissions={[EPermission.Allow]}
                    roles={productMenu?.roles || []}
                  />
                }
              />
              <Route
                path="product-allocations-files"
                element={
                  <ProtectedRoute
                    element={<ProductAllocationFiles />}
                    permissions={[EPermission.Allow]}
                    roles={productMenu?.roles || []}
                  />
                }
              />
              <Route
                path="stock"
                element={
                  <ProtectedRoute
                    element={<Stock />}
                    permissions={[EPermission.Allow]}
                    roles={stockMenu?.roles || []}
                  />
                }
              />
              <Route
                path="customer"
                element={
                  <ProtectedRoute
                    element={<Customer />}
                    permissions={[EPermission.Allow]}
                    roles={customerMenu?.roles || []}
                  />
                }
              />
              <Route
                path="customer/:id"
                element={
                  <ProtectedRoute
                    element={<CustomerMaintain />}
                    permissions={[EPermission.Allow]}
                    roles={customerMenu?.roles || []}
                  />
                }
              />
              <Route
                path="order"
                element={
                  <ProtectedRoute
                    element={<Order />}
                    permissions={[EPermission.Allow]}
                    roles={orderMenu?.roles || []}
                  />
                }
              />
              <Route
                path="order/:id"
                element={
                  <ProtectedRoute
                    element={<OrderDetail />}
                    permissions={[EPermission.Allow]}
                    roles={orderMenu?.roles || []}
                  />
                }
              />
              <Route
                path="export"
                element={
                  <ProtectedRoute
                    element={<Exportor />}
                    permissions={[EPermission.Allow]}
                    roles={exportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="export/:id"
                element={
                  <ProtectedRoute
                    element={<ExportDetail />}
                    permissions={[EPermission.Allow]}
                    roles={exportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="order/create"
                element={
                  <ProtectedRoute
                    element={<CreateOrder />}
                    permissions={[EPermission.Allow]}
                    roles={orderMenu?.roles || []}
                  />
                }
              />
              <Route
                path="export/create"
                element={
                  <ProtectedRoute
                    element={<CreateExportOrder />}
                    permissions={[EPermission.Allow]}
                    roles={exportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="inbound"
                element={
                  <ProtectedRoute
                    element={<Inbound />}
                    permissions={[EPermission.Allow]}
                    roles={inboundMenu?.roles || []}
                  />
                }
              />
              <Route
                path="inbound/create"
                element={
                  <ProtectedRoute
                    element={<InboundMaintain />}
                    permissions={[EPermission.Allow]}
                    roles={inboundMenu?.roles || []}
                  />
                }
              />
              <Route
                path="return"
                element={
                  <ProtectedRoute
                    element={<Return />}
                    permissions={[EPermission.Allow]}
                    roles={returnMenu?.roles || []}
                  />
                }
              />
              <Route
                path="location"
                element={
                  <ProtectedRoute
                    element={<Location />}
                    permissions={[EPermission.Allow]}
                    roles={locationMenu?.roles || []}
                  />
                }
              />
              <Route
                path="gr"
                element={
                  <ProtectedRoute
                    element={<GoodReceipt />}
                    permissions={[EPermission.Allow]}
                    roles={inboundMenu?.roles || []}
                  />
                }
              />
              <Route
                path="gr/create"
                element={
                  <ProtectedRoute
                    element={<GoodReceiptCreate />}
                    permissions={[EPermission.Allow]}
                    roles={inboundMenu?.roles || []}
                  />
                }
              />
              <Route
                path="collector"
                element={
                  <ProtectedRoute
                    element={<Collector />}
                    permissions={[EPermission.Allow]}
                    roles={collectorMenu?.roles || []}
                  />
                }
              />
              <Route
                path="unit-setting"
                element={
                  <ProtectedRoute
                    element={<UnitSetting />}
                    permissions={[EPermission.Allow]}
                    roles={baseUnitReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="email-follow-up"
                element={
                  <ProtectedRoute
                    element={<EmailFollowUpSetting />}
                    permissions={[EPermission.Allow]}
                    roles={baseUnitReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="business-unit"
                element={
                  <ProtectedRoute
                    element={<BusinessUnit />}
                    permissions={[EPermission.Allow]}
                    roles={buMenu?.roles || []}
                  />
                }
              />
              <Route
                path="company"
                element={
                  <ProtectedRoute
                    element={<CompanySetting />}
                    permissions={[EPermission.Allow]}
                    roles={companyReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="import-data"
                element={
                  <ProtectedRoute
                    element={<ImportData />}
                    permissions={[EPermission.Allow]}
                    roles={importDataMenu?.roles || []}
                  />
                }
              />
              {/* <Route
                path="role"
                element={
                  <ProtectedRoute
                    element={<RoleSetting />}
                    permissions={[EPermission.Allow]}
                  />
                }
              /> */}
              <Route
                path="user"
                element={
                  <ProtectedRoute
                    element={<UserSetting />}
                    permissions={[EPermission.Allow]}
                    roles={userDataMenu?.roles || []}
                  />
                }
              />
              <Route
                path="sales-report"
                element={
                  <ProtectedRoute
                    element={<SalesReport />}
                    permissions={[EPermission.Allow]}
                    roles={stockReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="sales-report-2"
                element={
                  <ProtectedRoute
                    element={<SalesReport2 />}
                    permissions={[EPermission.Allow]}
                    roles={stockReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="stock-report"
                element={
                  <ProtectedRoute
                    element={<StockReport />}
                    permissions={[EPermission.Allow]}
                    roles={stockReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="do-report"
                element={
                  <ProtectedRoute
                    element={<DoOrderReport />}
                    permissions={[EPermission.Allow]}
                    roles={stockReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="collector-report"
                element={
                  <ProtectedRoute
                    element={<CollectorReport />}
                    permissions={[EPermission.Allow]}
                    roles={collectorReportMenu?.roles || []}
                  />
                }
              />
              <Route
                path="customer-order-report"
                element={
                  <ProtectedRoute
                    element={<CustomerOrderReport />}
                    permissions={[EPermission.Allow]}
                    roles={customerOrderRerport?.roles || []}
                  />
                }
              />
              <Route
                path="financial-report"
                element={
                  <ProtectedRoute
                    element={<FinancialReport />}
                    permissions={[EPermission.Allow]}
                    roles={customerOrderRerport?.roles || []}
                  />
                }
              />
              <Route
                path="tax-report"
                element={
                  <ProtectedRoute
                    element={<SalesReport />}
                    permissions={[EPermission.Allow]}
                    roles={salesReport?.roles || []}
                  />
                }
              />
              <Route
                path="financial-matching-report"
                element={
                  <ProtectedRoute
                    element={<FinancialMatchingReport />}
                    permissions={[EPermission.Allow]}
                    roles={salesReport?.roles || []}
                  />
                }
              />
              <Route
                path="stock-matching-report"
                element={
                  <ProtectedRoute
                    element={<StockMatchingReport />}
                    permissions={[EPermission.Allow]}
                    roles={salesReport?.roles || []}
                  />
                }
              />
              <Route
                path="note-purchase"
                element={
                  <ProtectedRoute
                    element={<NotePurchaseReport />}
                    permissions={[EPermission.Allow]}
                    roles={salesReport?.roles || []}
                  />
                }
              />
              <Route
                path="exchange-rate"
                element={
                  <ProtectedRoute
                    element={<ExchangeRate />}
                    permissions={[EPermission.Allow]}
                    roles={userDataMenu?.roles || []}
                  />
                }
              />
              <Route
                path="cndn-note"
                element={
                  <ProtectedRoute
                    element={<CndnNote />}
                    permissions={[EPermission.Allow]}
                    roles={cndnNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="cndn-note/create"
                element={
                  <ProtectedRoute
                    element={<CreateCndn />}
                    permissions={[EPermission.Allow]}
                    roles={cndnNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="cndn-note/:id"
                element={
                  <ProtectedRoute
                    element={<ViewCndn />}
                    permissions={[EPermission.Allow]}
                    roles={cndnNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="billing-note"
                element={
                  <ProtectedRoute
                    element={<BillingNote />}
                    permissions={[EPermission.Allow]}
                    roles={billingNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="billing-note/create"
                element={
                  <ProtectedRoute
                    element={<CreateBillingNote />}
                    permissions={[EPermission.Allow]}
                    roles={billingNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="billing-note/:id"
                element={
                  <ProtectedRoute
                    element={<ViewBillingNote />}
                    permissions={[EPermission.Allow]}
                    roles={billingNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="billing-note/:id/edit"
                element={
                  <ProtectedRoute
                    element={<CreateBillingNote />}
                    permissions={[EPermission.Allow]}
                    roles={billingNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="free-form"
                element={
                  <ProtectedRoute
                    element={<FreeForm />}
                    permissions={[EPermission.Allow]}
                    roles={freeFormMenu?.roles || []}
                  />
                }
              />
              <Route
                path="order-free-form"
                element={
                  <ProtectedRoute
                    element={<OrderFreeFrom />}
                    permissions={[EPermission.Allow]}
                    roles={freeFormMenu?.roles || []}
                  />
                }
              />
              <Route
                path="order-free-form/create"
                element={
                  <ProtectedRoute
                    element={<CrateOrderFreeForm />}
                    permissions={[EPermission.Allow]}
                    roles={freeFormMenu?.roles || []}
                  />
                }
              />
              <Route
                path="order-free-form/:id"
                element={
                  <ProtectedRoute
                    element={<OrderManualDetail />}
                    permissions={[EPermission.Allow]}
                    roles={freeFormMenu?.roles || []}
                  />
                }
              />
              <Route
                path="cndn-free-form"
                element={
                  <ProtectedRoute
                    element={<CndnFreeForm />}
                    permissions={[EPermission.Allow]}
                    roles={freeFormMenu?.roles || []}
                  />
                }
              />
              <Route
                path="cndn-free-form/create"
                element={
                  <ProtectedRoute
                    element={<CreateCndnFreeForm />}
                    permissions={[EPermission.Allow]}
                    roles={cndnFreeFormMenu?.roles || []}
                  />
                }
              />
              <Route
                path="cndn-free-form/:id"
                element={
                  <ProtectedRoute
                    element={<ViewCndnFreeform />}
                    permissions={[EPermission.Allow]}
                    roles={cndnNoteMenu?.roles || []}
                  />
                }
              />
              <Route
                path="reserved"
                element={
                  <ProtectedRoute
                    element={<Reserved />}
                    permissions={[EPermission.Allow]}
                    roles={orderMenu?.roles || []}
                  />
                }
              />
              <Route
                path="reserved/:id"
                element={
                  <ProtectedRoute
                    element={<MaintainReserved />}
                    permissions={[EPermission.Allow]}
                    roles={orderMenu?.roles || []}
                  />
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </AppLayout>
        }
      />
    </Routes>
  );
};

export default RoutesConfig;
